import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import styled from '@emotion/styled';
import { Select, MenuItem } from '@mui/material';

import {
  FilePicker,
  FilePickerRef,
  getCdnPath,
  getS3Path,
  getWidgetCdnPath,
  useErrorNotification,
  useRetailerFileUpload
} from '../../framework';

const BackgroundSelect = styled(Select<string>)`
  .MuiSelect-select {
    display: flex;
    align-items: center;
    position: relative;
    padding-left: 176px;
    overflow: hidden;
    height: 80px;

    img {
      position: absolute;
      top: 0;
      left: 8px;
      max-width: 160px;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const BackgroundRow = styled(MenuItem)`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 176px;
  overflow: hidden;
  height: 80px;

  img {
    position: absolute;
    top: 0;
    left: 8px;
    max-width: 160px;
    height: 100%;
    object-fit: cover;
  }
`;

const backgrounds = [
  {
    name: 'White',
    url: `${getWidgetCdnPath()}/virtual-try-on/backgrounds/white.jpg`
  },
  {
    name: 'Studio',
    url: `${getWidgetCdnPath()}/virtual-try-on/backgrounds/studio.jpg`
  },
  {
    name: 'Abstract',
    url: `${getWidgetCdnPath()}/virtual-try-on/backgrounds/abstract.jpg`
  },
  {
    name: 'Dock',
    url: `${getWidgetCdnPath()}/virtual-try-on/backgrounds/dock.jpg`
  },
  {
    name: 'Street',
    url: `${getWidgetCdnPath()}/virtual-try-on/backgrounds/street.jpg`
  },
  {
    name: 'Beach',
    url: `${getWidgetCdnPath()}/virtual-try-on/backgrounds/beach.jpg`
  }
];

interface BackgroundImageFieldProps {
  value?: string;
  onChange: (value?: string) => void;
  retailerSlug: string;
  highlighted?: boolean;
}

export function BackgroundImageField(props: BackgroundImageFieldProps) {
  const { value, onChange, retailerSlug, highlighted } = props;

  const filePickerRef = useRef<FilePickerRef>(null);

  const { t } = useTranslation();
  const { displayErrors } = useErrorNotification();
  const { put, error } = useRetailerFileUpload({
    slug: retailerSlug,
    fileTag: `custom-background-${Date.now()}`
  });

  const onBackgroundChosen = (e: SelectChangeEvent<string>) => {
    // don't emit here because it emits on handleFileSelected
    if (e.target.value === 'custom') return;

    onChange(e.target.value);
  };

  const handleFileSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const result = await put(file);

    onChange(result?.cdnUrl);
  };

  const selectedValue = useMemo(() => {
    // default to White
    if (!value) {
      return `${getWidgetCdnPath()}/virtual-try-on/backgrounds/white.jpg`;
    }

    if (backgrounds.some(p => p.url === value)) {
      return value;
    }

    return 'custom';
  }, [value]);

  const customImageSrc = useMemo(
    () => value?.replace(getCdnPath(), getS3Path()) ?? '',
    [value]
  );

  useEffect(() => {
    if (error) {
      displayErrors([error]);
    }
  }, [error, displayErrors]);

  return (
    <>
      <BackgroundSelect
        fullWidth
        onChange={onBackgroundChosen}
        value={selectedValue}
      >
        {backgrounds.map(background => (
          <BackgroundRow key={background.name} value={background.url}>
            <img src={background.url} alt={background.name} />
            {background.name}
          </BackgroundRow>
        ))}
        <BackgroundRow
          value="custom"
          onClick={() => filePickerRef.current?.open()}
        >
          <img src={customImageSrc} alt={t('custom')} />
          {t('custom')}
        </BackgroundRow>
      </BackgroundSelect>
      <FilePicker
        ref={filePickerRef}
        onFileSelected={handleFileSelected}
        style={{ display: 'none' }}
        accept={'image/*'}
      />
    </>
  );
}
